import katex from 'katex';

import { safeDecodeHtmlEntities } from './htmlEntityDecoder';

const CURRENCY_DOLLAR_PLACEHOLDER = '__CURRENCY_DOLLAR__';

function normalizeHtmlContent(htmlContent: string): string {
  const hasRealHtmlTags = /<\/?[a-z][\s\S]*?>/i.test(htmlContent);
  const hasEncodedHtmlTags = /&lt;\/?[a-z][^&]*&gt;/i.test(htmlContent);

  if (!hasRealHtmlTags && hasEncodedHtmlTags) {
    let normalized = safeDecodeHtmlEntities(htmlContent);

    // Handle double-escaped HTML (e.g. &amp;lt;p&amp;gt;...).
    if (/&lt;\/?[a-z][^&]*&gt;/i.test(normalized)) {
      normalized = safeDecodeHtmlEntities(normalized);
    }

    return normalized;
  }

  return htmlContent;
}

/**
 * Decodes HTML-encoded dollar-sign entities to literal `$` so that
 * `protectCurrencyDollars` can protect them before any math regex runs.
 * Handles the most common encodings: &#36;  &#x24;  &dollar;
 */
function decodeHtmlDollarEntities(content: string): string {
  return content
    .replace(/&#36;/g, '$')
    .replace(/&#x24;/gi, '$')
    .replace(/&dollar;/gi, '$');
}

function protectCurrencyDollars(content: string): string {
  // Prevent "$20 ... $10" currency amounts from being treated as LaTeX delimiters.
  // Lookahead covers: whitespace, HTML tags (<), newlines, end-of-string, and common
  // punctuation/operator characters so that e.g. "$800 - $100" and "$100</p>" are
  // both fully protected before the math regex runs.
  return content.replace(
    /(?<!\\)\$(\d+(?:[.,]\d+)?)(?=\s|$|<|\n|[-+*/=),.;:!?\]])/g,
    (_match, amount) => `${CURRENCY_DOLLAR_PLACEHOLDER}${amount}`
  );
}

function restoreCurrencyDollars(content: string): string {
  return content.split(CURRENCY_DOLLAR_PLACEHOLDER).join('$');
}

/**
 * Escapes underscores within \text{} blocks to prevent KaTeX parse errors
 */
function escapeUnderscoresInText(mathContent: string): string {
  return mathContent.replace(/\\text\{([^}]*)\}/g, (_match, textContent) => {
    const escapedContent = textContent.replace(/_/g, '\\_');
    return `\\text{${escapedContent}}`;
  });
}

/**
 * Fixes invalid \cancel usage in superscripts/subscripts
 * Converts patterns like }^\cancel{2} to }^{\cancel{2}} or removes them
 */
function fixCancelInScripts(mathContent: string): string {
  // Fix \cancel used directly as superscript: }^\cancel{...}
  mathContent = mathContent.replace(/\}\^\s*\\cancel\{([^}]*)\}/g, '}');
  // Fix \cancel used directly as subscript: }_\cancel{...}
  mathContent = mathContent.replace(/\}_\s*\\cancel\{([^}]*)\}/g, '}');
  return mathContent;
}

/**
 * Removes problematic combining characters that cause KaTeX parsing errors.
 */
export function sanitizeMathContent(mathContent: string): string {
  // Remove common combining diacritics (U+0300–U+036F)
  return mathContent.replace(/[\u0300-\u036F]/g, '');
}

/**
 * Pre-processes math content to ensure compatibility with KaTeX.
 * Handles currency symbols and cleans up invalid characters.
 */
export function preprocessMathContent(mathContent: string): string {
  // 1. Restore protected currency placeholders inside the math block
  let processed = restoreCurrencyDollars(mathContent.trim());

  // 2. Normalize smart quotes to standard quotes for \text{} blocks
  processed = processed.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");

  // 3. Escape literal dollar signs that aren't already escaped
  // This allows currency ($800) to be rendered safely within math modes
  processed = processed.replace(/(?<!\\)\$/g, '\\$');

  // 4. Remove problematic combining characters
  processed = sanitizeMathContent(processed);

  return processed;
}

// Stash rendered KaTeX HTML and replace with opaque placeholders so
// subsequent regex passes cannot accidentally match content inside them.
const RENDERED_MATH_PLACEHOLDER_PREFIX = '__RENDERED_MATH_';
const RENDERED_MATH_PLACEHOLDER_SUFFIX = '__';

function stashRenderedMath(content: string, stash: string[]): string {
  // Walk the string and replace KaTeX output blocks with opaque placeholders.
  // KaTeX always wraps output in <span class="katex"> (inline) or
  // <span class="katex-display"> (block). We use span-depth counting instead
  // of a regex so deeply-nested spans are captured correctly.
  const KATEX_START = '<span class="katex';
  let result = '';
  let searchPos = 0;

  while (searchPos < content.length) {
    const katexStart = content.indexOf(KATEX_START, searchPos);
    if (katexStart === -1) {
      result += content.slice(searchPos);
      break;
    }

    result += content.slice(searchPos, katexStart);

    // Count <span> opens and </span> closes to find the balanced end.
    let depth = 0;
    let pos = katexStart;
    let blockEnd = -1;

    while (pos < content.length) {
      const nextOpen = content.indexOf('<span', pos);
      const nextClose = content.indexOf('</span>', pos);

      if (nextClose === -1) break; // Malformed HTML — bail.

      if (nextOpen !== -1 && nextOpen < nextClose) {
        depth++;
        pos = nextOpen + '<span'.length;
      } else {
        depth--;
        pos = nextClose + '</span>'.length;
        if (depth === 0) {
          blockEnd = pos;
          break;
        }
      }
    }

    if (blockEnd === -1) {
      // Unbalanced HTML — skip stashing to avoid data loss.
      result += content.slice(katexStart);
      break;
    }

    const renderedBlock = content.slice(katexStart, blockEnd);
    const idx = stash.length;
    stash.push(renderedBlock);
    result += `${RENDERED_MATH_PLACEHOLDER_PREFIX}${idx}${RENDERED_MATH_PLACEHOLDER_SUFFIX}`;
    searchPos = blockEnd;
  }

  return result;
}

function restoreRenderedMath(content: string, stash: string[]): string {
  return content.replace(
    new RegExp(`${RENDERED_MATH_PLACEHOLDER_PREFIX}(\\d+)${RENDERED_MATH_PLACEHOLDER_SUFFIX}`, 'g'),
    (_match, idxStr) => stash[parseInt(idxStr, 10)] ?? _match
  );
}

export function renderMathInHtml(htmlContent: string, formatVersion?: number): string {
  if (!htmlContent) return htmlContent;

  // Regular expressions to match LaTeX expressions
  // Dollar sign notation (primary)
  const blockMathRegex = /\$\$([\s\S]*?)\$\$/g;
  const inlineMathWithBracketsRegex = /\$\\\[([\s\S]*?)\\\]\$/g;
  // Tighten inline math regex to require non-space boundaries, reducing currency false positives
  const inlineMathRegex = /(?<!\$)\$(?!\s)([\s\S]*?)(?<!\s)\$(?!\$)/g;

  // Legacy notation (fallback for backwards compatibility)
  const legacyBlockMathInBracketsRegex = /\(\\\[([\s\S]*?)\\\]\)/g;
  const legacyInlineMathRegex = /\\\(([\s\S]*?)\\\)/g;
  const legacyBlockMathRegex = /\\\[([\s\S]*?)\\\]/g;

  // Stash for rendered KaTeX HTML — prevents later regex passes from
  // accidentally treating content inside already-rendered blocks as math.
  const renderedStash: string[] = [];

  let processedContent = normalizeHtmlContent(htmlContent);

  // ─── PHASE 1: Process <latex> tags (primary path for new content) ───
  const blockLatexTagRegex = /<latex\s+display="block">([\s\S]*?)<\/latex>/g;
  const inlineLatexTagRegex = /<latex>([\s\S]*?)<\/latex>/g;

  // Block math: <latex display="block">...</latex>
  processedContent = processedContent.replace(blockLatexTagRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);
      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: true,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render <latex display="block">:', mathContent, error);
      return match;
    }
  });
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // Inline math: <latex>...</latex>
  processedContent = processedContent.replace(inlineLatexTagRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);
      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: false,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render <latex>:', mathContent, error);
      return match;
    }
  });
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // v2 content uses <latex> tags exclusively — skip all legacy patterns
  if (formatVersion && formatVersion >= 2) {
    return restoreRenderedMath(processedContent, renderedStash);
  }

  // ─── PHASE 2: Legacy $...$ patterns (for existing content in Firestore) ───
  // Decode HTML-encoded dollar entities (e.g. &#36;) so that they are
  // protected by protectCurrencyDollars just like literal $ signs.
  processedContent = decodeHtmlDollarEntities(processedContent);
  processedContent = protectCurrencyDollars(processedContent);

  // Process block math first ($$...$$)
  processedContent = processedContent.replace(blockMathRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);

      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: true,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render block math:', mathContent, error);
      return match;
    }
  });
  // Stash rendered KaTeX so the inline regex cannot reach inside it.
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // Process inline math with brackets ($\[...\]$)
  processedContent = processedContent.replace(inlineMathWithBracketsRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);

      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: false,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render inline math with brackets:', mathContent, error);
      return match;
    }
  });
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // Process regular inline math ($...$)
  processedContent = processedContent.replace(inlineMathRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);

      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: false,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render inline math:', mathContent, error);
      return match;
    }
  });
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // Legacy patterns (backwards compatibility)
  // Process (\[...\]) notation
  processedContent = processedContent.replace(legacyBlockMathInBracketsRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);
      const renderedMath = katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: false,
        output: 'html'
      });
      return `(${renderedMath})`;
    } catch (error) {
      console.warn('Failed to render legacy block math in brackets:', mathContent, error);
      return match;
    }
  });
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // Process \(...\) notation
  processedContent = processedContent.replace(legacyInlineMathRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);
      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: false,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render legacy inline math:', mathContent, error);
      return match;
    }
  });
  processedContent = stashRenderedMath(processedContent, renderedStash);

  // Process \[...\] notation
  processedContent = processedContent.replace(legacyBlockMathRegex, (match, mathContent) => {
    try {
      let processedMath = safeDecodeHtmlEntities(mathContent);
      processedMath = preprocessMathContent(processedMath);
      processedMath = escapeUnderscoresInText(processedMath);
      processedMath = fixCancelInScripts(processedMath);
      return katex.renderToString(processedMath, {
        throwOnError: false,
        displayMode: true,
        output: 'html'
      });
    } catch (error) {
      console.warn('Failed to render legacy block math:', mathContent, error);
      return match;
    }
  });

  // Restore all stashed KaTeX HTML, then restore currency dollar signs.
  processedContent = restoreRenderedMath(processedContent, renderedStash);
  return restoreCurrencyDollars(processedContent);
}
